import { ItemsArray, RssItem } from "./index";
import { rssFeedContent } from "./rssFeed";
import { Attributes, Element } from "xml-js";
import { Lens, Optional, Prism } from "monocle-ts";
import { array } from "fp-ts/lib/Array";
import { fromTraversable } from "monocle-ts/lib/Traversal";
import { none, Option, some } from "fp-ts/lib/Option";
import * as Opt from "fp-ts/lib/Option";
import { pipe } from "fp-ts/lib/function";
import { filter } from "fp-ts/lib/Array";
import { map } from "fp-ts/lib/Array";

// Täältä referenssiä, miten filteröidään tietyn nimiset elementit taulukosta
// https://github.com/gcanti/monocle-ts/issues/77
export const elementsLens = Lens.fromProp<Element>()("elements");

// TODO move all the PI-related functions to own module

// TODO add xmlns:podcast="https://github.com/Podcastindex-org/podcast-namespace/blob/main/docs/1.0.md" if it does not exist

export const optionalElements: (element: Element) => Option<Element[]> = (
  element: Element
) => (!!element.elements ? some(element.elements) : none);
export const setOptionalElements: (
  elements: Element[]
) => (el: Element) => Element = (elements: Element[]) => (el: Element) =>
  !!elements ? { ...el, elements } : el;
export const elementsOptional: Optional<Element, Element[]> = new Optional<
  Element,
  Element[]
>(optionalElements, setOptionalElements);

export const optionalElementByName = (name: string) => (
  elements: Element[]
) => {
  const el = elements.find((e) => e.name === name);
  return el ? some(el) : none;
};
export const setOptionalElementByName = (name: string) => (
  element: Element
) => (elements: Element[]) =>
  elements.map((e) => (e.name === name ? element : e));
export const elementByNameOptional: (
  name: string
) => Optional<Element[], Element> = (name: string) =>
  new Optional<Element[], Element>(
    optionalElementByName(name),
    setOptionalElementByName(name)
  );

export const optionalElementByType = (type: string) => (
  elements: Element[]
) => {
  const el = elements.find((e) => e.type === type);
  return el ? some(el) : none;
};
export const setOptionalElementByType = (type: string) => (
  element: Element
) => (elements: Element[]) =>
  elements.map((e) => (e.type === type ? element : e));
export const elementByTypeOptional: (
  type: string
) => Optional<Element[], Element> = (type: string) =>
  new Optional<Element[], Element>(
    optionalElementByType(type),
    setOptionalElementByType(type)
  );

export const optionalAttributeByName = (name: string) => (element: Element) => {
  const attr = element?.attributes?.[name];
  return attr ? some(attr) : none;
};
export const setOptionalAttributeByName = (name: string) => (
  attr: string | number | undefined
) => (element: Element) =>
  element?.attributes
    ? { ...element, attributes: { ...element.attributes, [name]: attr } }
    : element;
export const attributeByNameOptional: (
  name: string
) => Optional<Element, string | number | undefined> = (name: string) =>
  new Optional<Element, string | number | undefined>(
    optionalAttributeByName(name),
    setOptionalAttributeByName(name)
  );

export const channelLens = elementsOptional
  .composeOptional(elementByNameOptional("rss"))
  .composeOptional(elementsOptional)
  .composeOptional(elementByNameOptional("channel"));

export const elementTextLens = elementsOptional
  .composeOptional(elementByTypeOptional("text"))
  .composeLens(Lens.fromProp<Element>()("text"));

export const channelTitleLens = channelLens
  .composeOptional(elementsOptional)
  .composeOptional(elementByNameOptional("title"))
  .composeOptional(elementTextLens);
export const getChannelTitle = (feed: Element): string =>
  (Opt.toNullable(channelTitleLens.getOption(feed)) || "") + "";

export const titleLens = elementsOptional
  .composeOptional(elementByNameOptional("title"))
  .composeOptional(elementTextLens);

export const chapterLinkLens = elementsOptional.composeOptional(
  elementByNameOptional("podcast:chapters")
);
export const getChaptersLink = (el: Element) => {
  return pipe(
    chapterLinkLens.getOption(el),
    Opt.fold(
      () => null,
      (el: Element) => {
        const mime = yankString(attributeByNameOptional("type").getOption(el));
        const url = yankString(attributeByNameOptional("url").getOption(el));
        return { mime, url };
      }
    )
  );
};

export const getTranscriptLinks = (el: Element) => {
  return (
    el.elements
      ?.filter((e) => e.name === "podcast:transcript")
      .map((el: Element) => {
        const mime = yankString(attributeByNameOptional("type").getOption(el));
        const url = yankString(attributeByNameOptional("url").getOption(el));
        const rel = yankString(attributeByNameOptional("rel").getOption(el));
        const language = yankString(
          attributeByNameOptional("language").getOption(el)
        );

        return { mime, url, rel, language };
      }) || []
  );
};

export const getSoundbites = (el: Element) => {
  return (
    el.elements
      ?.filter((e) => e.name === "podcast:soundbite")
      .map((el: Element) => {
        console.log("SOUNDBITE", el);
        const text = yankString(textByNameLens("text").getOption(el));
        const start = parseFloat(
          yankString(attributeByNameOptional("startTime").getOption(el))
        );
        const duration = parseFloat(
          yankString(attributeByNameOptional("duration").getOption(el))
        );

        return { text, start, duration };
      }) || []
  );
};

export const guidLens = elementsOptional
  .composeOptional(elementByNameOptional("guid"))
  .composeOptional(elementTextLens);

// TODO get rid of channelLens here and compose elsewhere
export const rootTextByNameLens = (name: string) =>
  channelLens
    .composeOptional(elementsOptional)
    .composeOptional(elementByNameOptional(name))
    .composeOptional(elementTextLens);

export const textByNameLens = (name: string) =>
  elementsOptional
    .composeOptional(elementByNameOptional(name))
    .composeOptional(elementTextLens);
export const attributesLens = Lens.fromProp<Element>()("attributes");

export const channelDescriptionLens = channelLens
  .composeOptional(elementsOptional)
  .composeOptional(elementByNameOptional("description"));
export const getChannelDescription = (feed: Element) =>
  (Opt.toNullable(channelDescriptionLens.getOption(feed)) || "") + "";

export const imageHrefLens = channelLens
  .composeOptional(elementsOptional)
  .composeOptional(elementByNameOptional("itunes:image"))
  .composeOptional(attributeByNameOptional("href"));
export const getImgUrl = (feed: Element) =>
  (Opt.toNullable(imageHrefLens.getOption(feed)) || "") + "";

export const lockedTextLens = channelLens
  .composeOptional(elementsOptional)
  .composeOptional(elementByNameOptional("podcast:locked"))
  .composeOptional(elementTextLens);
export const getChannelLocked = (feed: Element) =>
  Opt.toNullable(lockedTextLens.getOption(feed)) || "no";

export const lockedOwnerAttrLens = channelLens
  .composeOptional(elementsOptional)
  .composeOptional(elementByNameOptional("podcast:locked"))
  .composeOptional(attributeByNameOptional("owner"));
export const getChannelLockedOwner = (feed: Element) =>
  (Opt.toNullable(lockedOwnerAttrLens.getOption(feed)) || "") + "";

export const channelElementsLens = channelLens.composeOptional(
  elementsOptional
);

export const upsertLockedTag = (
  locked: boolean,
  owner: string,
  rssFeedContent: Element
): Element => {
  return channelElementsLens.modify((elements: Element[]): Element[] => {
    const found = elements.find(
      (element) => element?.name === "podcast:locked"
    );
    const newLocked: Element = {
      name: "podcast:locked",
      attributes: { owner },
      elements: [{ type: "text", text: locked ? "yes" : "no" }],
      type: "element",
    };
    console.log("Setting locked", newLocked, found, locked);
    if (found) {
      return elements.map((el) =>
        el.name === "podcast:locked" ? newLocked : el
      );
    } else {
      return [...elements, newLocked];
    }
  })(rssFeedContent);
};

export const getItems = (rssFeedContent: Element): ItemsArray => {
  const elements: Option<Element[]> = channelElementsLens.getOption(
    rssFeedContent
  );
  return pipe(
    elements,
    Opt.map(filter((el: Element) => el.name === "item")),
    Opt.getOrElse<Element[]>(() => []),
    map(elementToItem)
  );
};

export const yankString = (
  opt: Option<string | number | boolean | undefined>
) => (Opt.toNullable(opt) || "") + "";

export const elementToItem = (el: Element): RssItem => {
  console.log("Transcripts", getTranscriptLinks(el));

  return {
    title: yankString(titleLens.getOption(el)),
    guid: yankString(guidLens.getOption(el)),
    chapterLink: getChaptersLink(el),

    imageUrl:
      Opt.getOrElse<string | number | undefined>(() => "")(
        elementsOptional
          .composeOptional(elementByNameOptional("itunes:image"))
          .composeOptional(attributeByNameOptional("href"))
          .getOption(el)
      ) + "",
    transcripts: getTranscriptLinks(el),
    soundbites: getSoundbites(el),
  };
};

// export const elementsTravelsal = fromTraversable(array)<Element>();

// export const getElementPrism = (
//   name: string
// ): Prism<Element | undefined, Element> =>
//   Prism.fromPredicate((element) => (!element ? false : element.name === name));

//

// export const channelLens = elementsLens
//   .composeTraversal<Element>(elementsTravelsal)
//   .composePrism(getElementPrism("channel"));
